import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Pencil } from 'lucide-react'
import {
  fetchStaffDirectory,
  fetchStaffMember,
  formatStaffDate,
} from '../../../services/staffDirectoryService'
import { getAdminAuthToken } from '../../../services/adminAuthService'
import StaffStatusBadge from './StaffStatusBadge'
import StaffFormModal from './StaffFormModal'
import StaffActionsModal from './StaffActionsModal'

export default function StaffDetailPage() {
  const { staffId } = useParams()
  const navigate = useNavigate()
  const [member, setMember] = useState(null)
  const [options, setOptions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [editOpen, setEditOpen] = useState(false)
  const [statusOpen, setStatusOpen] = useState(false)

  const load = useCallback(async () => {
    if (!getAdminAuthToken()) {
      setError('Please sign in to view this staff member.')
      setLoading(false)
      return
    }
    setLoading(true)
    setError('')
    try {
      const result = await fetchStaffMember(staffId)
      setMember(result)
    } catch (err) {
      setMember(null)
      setError(err.response?.status === 404 ? 'Staff member not found.' : (err.response?.data?.message || 'Unable to load this staff member.'))
    } finally {
      setLoading(false)
    }
  }, [staffId])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    fetchStaffDirectory({ perPage: 100 })
      .then((result) => setOptions(result.staff.map((m) => ({ id: m.id, name: m.name, employeeId: m.employee_id }))))
      .catch(() => setOptions([]))
  }, [])

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col p-6">
      <Link to="/admin/staff" className="inline-flex w-fit items-center gap-2 text-sm font-semibold text-gray-500 transition-colors hover:text-gray-900">
        <ArrowLeft size={16} strokeWidth={2} />
        Back to Staff Directory
      </Link>

      {error ? (
        <div role="alert" className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {loading ? (
        <p className="mt-6 text-sm text-gray-500">Loading staff member…</p>
      ) : member ? (
        <>
          <header className="mt-4 flex flex-col gap-4 rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-100 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-4">
              <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-orange-100 text-lg font-bold text-rapid-orange">
                {member.initials}
              </span>
              <div>
                <h1 className="text-lg font-bold text-gray-900">{member.name}</h1>
                <p className="text-sm text-gray-500">
                  {member.jobTitle || 'No job title'}{member.department ? ` · ${member.department}` : ''}
                </p>
                <div className="mt-1 flex items-center gap-2">
                  <StaffStatusBadge status={member.status} label={member.statusLabel} />
                  <span className="text-xs text-gray-400">{member.employeeId}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setStatusOpen(true)}
                className="rounded-lg border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50"
              >
                Change status
              </button>
              <button
                type="button"
                onClick={() => setEditOpen(true)}
                className="inline-flex items-center gap-2 rounded-lg bg-rapid-orange px-3 py-2 text-sm font-bold text-white transition-colors hover:bg-[#E84A15]"
              >
                <Pencil size={15} strokeWidth={2} />
                Edit
              </button>
            </div>
          </header>

          <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Card title="Personal information">
              <Detail label="Employee ID" value={member.employeeId} />
              <Detail label="Email" value={member.email} />
              <Detail label="Gender" value={member.gender} />
              <Detail label="Date of birth" value={formatStaffDate(member.dateOfBirth)} />
              <Detail label="Manager" value={member.managerName} />
            </Card>

            <Card title="Employment">
              <Detail label="Department" value={member.department} />
              <Detail label="Job title" value={member.jobTitle} />
              <Detail label="Employment type" value={member.employmentTypeLabel} />
              <Detail label="Status" value={member.statusLabel} />
              <Detail label="Hire date" value={formatStaffDate(member.hireDate)} />
              <Detail label="End date" value={member.endDate ? formatStaffDate(member.endDate) : null} />
            </Card>

            <Card title="Contact">
              <Detail label="Phone" value={member.phone} />
              <Detail label="Alternate phone" value={member.altPhone} />
              <Detail label="Address" value={member.address} />
              <Detail label="City" value={member.city} />
              <Detail label="State" value={member.state} />
              <Detail label="Country" value={member.country} />
            </Card>

            <Card title="Emergency contact">
              <Detail label="Name" value={member.emergencyContactName} />
              <Detail label="Phone" value={member.emergencyContactPhone} />
              <Detail label="Relationship" value={member.emergencyContactRelationship} />
            </Card>

            <Card title="Notes" full>
              <p className="whitespace-pre-wrap text-sm text-gray-700">{member.notes || 'No notes recorded.'}</p>
            </Card>
          </div>

          <StaffFormModal
            isOpen={editOpen}
            onClose={() => setEditOpen(false)}
            member={member}
            staffOptions={options}
            onSaved={load}
          />
          <StaffActionsModal
            isOpen={statusOpen}
            onClose={() => setStatusOpen(false)}
            action="status"
            member={member}
            onSuccess={() => load()}
          />
        </>
      ) : (
        <button
          type="button"
          onClick={() => navigate('/admin/staff')}
          className="mt-6 w-fit rounded-lg bg-gray-900 px-4 py-2 text-sm font-bold text-white"
        >
          Back to Staff Directory
        </button>
      )}
    </div>
  )
}

function Card({ title, children, full }) {
  return (
    <section className={`rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-100 ${full ? 'lg:col-span-2' : ''}`}>
      <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500">{title}</h2>
      <dl className="mt-3 space-y-2">{children}</dl>
    </section>
  )
}

function Detail({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <dt className="text-sm text-gray-500">{label}</dt>
      <dd className="text-right text-sm font-medium text-gray-900">{value || '—'}</dd>
    </div>
  )
}
